import React from 'react';
import FormInput from './FormInput';
import FormRadio from './FormRadio';
import FormSelect from './FormSelect';
import { validateCredentials } from './formUtils';
import { CONTACT_METHODS, URGENCY_LEVELS, TIMEFRAME_OPTIONS, REFERRAL_STATUS_OPTIONS, INTERNAL_STATUS_OPTIONS } from './formConstants';

const ReferralCoverPage = ({ formData, onDataUpdate, validationErrors }) => {
  const handleChange = (field, value) => {
    onDataUpdate({ [field]: value });
  };

  const errors = validationErrors || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center border-b-2 border-blue-600 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">OMHC & PRP Referral Form</h1>
        <p className="text-lg text-gray-600 mt-2">Adult and Minor Referral Packet</p>
        <p className="text-sm text-gray-500 mt-1">Version 1.0 | {new Date().toLocaleDateString()}</p>
      </div>

      {/* Referral Source Information */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-2">Referral Source Information</h2>
        {errors.coverPage && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="cover-page-errors">
            Please complete all required fields before proceeding.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Referral Date"
            type="date"
            value={formData.referralDate}
            onChange={(value) => handleChange('referralDate', value)}
            required
          />
          <FormInput
            label="Referral Source / Agency Name"
            value={formData.referralSource}
            onChange={(value) => handleChange('referralSource', value)}
            required
          />
          <FormInput
            label="Referring Provider Name"
            value={formData.referringProvider}
            onChange={(value) => handleChange('referringProvider', value)}
            required
          />
          <FormInput
            label="Credentials"
            value={formData.credentials}
            onChange={(value) => handleChange('credentials', value)}
            placeholder="e.g., MD, LCSW-C, NP"
            required
            error={errors.credentials || (formData.credentials ? validateCredentials(formData.credentials) : '')}
          />
          <div>
            <FormInput
              label="NPI Number"
              value={formData.npi}
              onChange={(value) => handleChange('npi', value)}
              required
              error={errors.npi}
            />
            <a
              href="https://npiregistry.cms.hhs.gov/search"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 underline mt-1 inline-block"
              data-testid="npi-lookup-link"
            >
              Search for your NPI Number
            </a>
          </div>
          <FormInput
            label="Phone"
            type="tel"
            value={formData.phone}
            onChange={(value) => handleChange('phone', value)}
            required
          />
          <FormInput
            label="Fax"
            type="tel"
            value={formData.fax}
            onChange={(value) => handleChange('fax', value)}
          />
          <FormInput
            label="Email"
            type="email"
            value={formData.email}
            onChange={(value) => handleChange('email', value)}
            required
            error={errors.email}
          />
        </div>

        <div className="mt-4">
          <FormRadio
            label="Best Contact Method"
            name="contactMethod"
            options={CONTACT_METHODS}
            value={formData.contactMethod}
            onChange={(value) => handleChange('contactMethod', value)}
          />
        </div>

        {/* Clinical Supervision */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <FormSelect
            label="Is the Referring Provider under Clinical Supervision?"
            value={formData.underSupervision || ''}
            onChange={(value) => {
              handleChange('underSupervision', value);
              if (value !== 'Yes') {
                handleChange('supervisorName', '');
                handleChange('supervisorCredentials', '');
                handleChange('supervisorNPI', '');
                handleChange('supervisorPhone', '');
                handleChange('supervisorEmail', '');
              }
            }}
            options={[{ value: '', label: 'Select...' }, { value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]}
            required
            error={errors.underSupervision}
          />

          {formData.underSupervision === 'Yes' && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200 space-y-3">
              <h3 className="text-sm font-bold text-blue-800 mb-2">Supervisor Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Supervisor's Full Name"
                  value={formData.supervisorName || ''}
                  onChange={(value) => handleChange('supervisorName', value)}
                  required
                  error={errors.supervisorName}
                />
                <FormInput
                  label="Credentials"
                  value={formData.supervisorCredentials || ''}
                  onChange={(value) => handleChange('supervisorCredentials', value)}
                  placeholder="e.g., LCSW-C, PhD, PsyD"
                  required
                  error={errors.supervisorCredentials}
                />
                <div>
                  <FormInput
                    label="NPI Number"
                    value={formData.supervisorNPI || ''}
                    onChange={(value) => handleChange('supervisorNPI', value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10-digit NPI"
                    required
                    error={errors.supervisorNPI || (formData.supervisorNPI && formData.supervisorNPI.replace(/\D/g, '').length > 0 && formData.supervisorNPI.replace(/\D/g, '').length !== 10 ? 'NPI must be exactly 10 digits' : '')}
                  />
                </div>
                <FormInput
                  label="Phone Number"
                  type="tel"
                  value={formData.supervisorPhone || ''}
                  onChange={(value) => handleChange('supervisorPhone', value)}
                  required
                  error={errors.supervisorPhone}
                />
                <FormInput
                  label="Email Address"
                  type="email"
                  value={formData.supervisorEmail || ''}
                  onChange={(value) => handleChange('supervisorEmail', value)}
                  required
                  error={errors.supervisorEmail || (formData.supervisorEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.supervisorEmail) ? 'Please enter a valid email address' : '')}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Referral Details */}
      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-2">Referral Details</h2>
        
        <div className="space-y-4">
          <FormRadio
            label="Urgency Level"
            name="urgencyLevel"
            options={URGENCY_LEVELS}
            value={formData.urgencyLevel}
            onChange={(value) => handleChange('urgencyLevel', value)}
            required
          />

          <FormRadio
            label="Client Type"
            name="clientType"
            options={['Adult', 'Minor']}
            value={formData.clientType}
            onChange={(value) => handleChange('clientType', value)}
            required
          />

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Referral Category <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.referralCategory || ''}
              onChange={(e) => handleChange('referralCategory', e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              data-testid="referral-category-select"
            >
              <option value="">Select Referral Category...</option>
              <option value="OMHC">OMHC (Outpatient Mental Health Clinic)</option>
              <option value="PRP">PRP (Psychiatric Rehabilitation Program)</option>
              <option value="Both">Both OMHC and PRP</option>
            </select>
            {formData.referralCategory && (
              <p className="text-xs text-blue-600 mt-1 font-medium">
                {formData.referralCategory === 'OMHC' && 'You will complete the OMHC referral form.'}
                {formData.referralCategory === 'PRP' && 'You will complete the PRP referral form.'}
                {formData.referralCategory === 'Both' && 'You will complete both OMHC and PRP referral forms.'}
              </p>
            )}
          </div>

          <FormSelect
            label="Preferred Start Timeframe"
            value={formData.preferredTimeframe}
            onChange={(value) => handleChange('preferredTimeframe', value)}
            options={TIMEFRAME_OPTIONS}
          />

          <FormSelect
            label="Referral Status"
            value={formData.referralStatus}
            onChange={(value) => handleChange('referralStatus', value)}
            options={REFERRAL_STATUS_OPTIONS}
          />
        </div>
      </section>

      {/* Office Use Only */}
      <section className="bg-gray-100 p-4 rounded-lg border-2 border-gray-300">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Office Use Only</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Date Received"
            type="date"
            value={formData.dateReceived}
            onChange={(value) => handleChange('dateReceived', value)}
          />
          <FormInput
            label="Intake Coordinator"
            value={formData.intakeCoordinator}
            onChange={(value) => handleChange('intakeCoordinator', value)}
          />
          <FormInput
            label="Internal Tracking ID"
            value={formData.trackingId}
            onChange={(value) => handleChange('trackingId', value)}
          />
          <FormSelect
            label="Status"
            value={formData.internalStatus}
            onChange={(value) => handleChange('internalStatus', value)}
            options={INTERNAL_STATUS_OPTIONS}
          />
        </div>
      </section>
    </div>
  );
};

export default ReferralCoverPage;
